import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { useAuctionReminders } from '~/composables/useAuctionReminders'
import { useServerClock } from '~/composables/useServerClock'
import type { Product } from '~/models/product'
import { useFavoritesStore } from '~/store/favorites'
import { isRecord, unwrapApiData } from '~/types/api'

const CHECK_INTERVAL_MS = 5 * 60 * 1000
const REMINDER_OFFSET_MS = 60 * 60 * 1000
const NOTIFIED_KEY = 'auction-reminder-notified'

dayjs.extend(utc)

const loadNotifiedMap = () => {
  if (!import.meta.client) return {}
  const raw = localStorage.getItem(NOTIFIED_KEY)
  return raw ? (JSON.parse(raw) as Record<string, string>) : {}
}

const saveNotifiedMap = (map: Record<string, string>) => {
  if (!import.meta.client) return
  localStorage.setItem(NOTIFIED_KEY, JSON.stringify(map))
}

const isNotFoundError = (e: unknown): boolean => {
  return (
    (isRecord(e) && e.statusCode === 404) ||
    (isRecord(e) && e.status === 404) ||
    (isRecord(e) && isRecord(e.response) && e.response.status === 404)
  )
}

const fetchProduct = async (
  apiUrl: string,
  productId: number,
  favoritesStore: ReturnType<typeof useFavoritesStore>,
  syncFromPayload: (payload: unknown) => boolean,
) => {
  try {
    const response = await $fetch<{ data: Product } | Product>(`${apiUrl}/api/v1/products/${productId.toString()}`, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    })
    syncFromPayload(response)
    return unwrapApiData<Product>(response)
  } catch (e: unknown) {
    if (isNotFoundError(e)) {
      void favoritesStore.methods.load()
      void favoritesStore.methods.remove(productId)
      const map = loadNotifiedMap()
      delete map[productId.toString()]
      saveNotifiedMap(map)
    }
    return null
  }
}

export default defineNuxtPlugin(() => {
  if (!import.meta.client || !('Notification' in window)) return

  const favoritesStore = useFavoritesStore()
  const { enabled, permission, load } = useAuctionReminders()
  const { nowMs, syncFromPayload } = useServerClock()
  const config = useRuntimeConfig()
  const apiUrl = config.public.apiUrl || config.public.apiBase || 'http://localhost:8000'

  let timer: number | null = null

  const sendReminder = (product: Product, endsAt: string) => {
    // Display in UTC to match the auction schedule shown across the platform
    // (cards/detail format with dayjs.utc to mirror the UTC admin picker).
    const timeLabel = `${dayjs.utc(endsAt).format('MMM D, h:mm A')} UTC`
    const notification = new Notification('Auction ending soon', {
      body: `${product.display_name} ends at ${timeLabel}.`,
      tag: `auction-${product.id.toString()}`,
    })
    notification.onclick = () => {
      window.open(`/products/${product.id.toString()}`, '_blank')
    }
  }

  const checkFavorites = async () => {
    if (!enabled.value || permission.value !== 'granted') return

    await favoritesStore.methods.load()
    const favorites = favoritesStore.data.items
    if (!favorites.length) return

    const notifiedMap = loadNotifiedMap()
    const now = nowMs()

    for (const favorite of favorites) {
      const productId = favorite.id
      const freshProduct = await fetchProduct(apiUrl, productId, favoritesStore, syncFromPayload)
      const product = freshProduct || favorite
      if (!product.auction_ends_at) continue

      const endsAt = product.auction_ends_at
      const endTime = new Date(endsAt).getTime()
      if (Number.isNaN(endTime)) continue
      if (now > endTime) continue

      const remindAt = endTime - REMINDER_OFFSET_MS
      if (now < remindAt) continue

      const notifiedKey = productId.toString()
      if (notifiedMap[notifiedKey] === endsAt) {
        continue
      }

      sendReminder(product, endsAt)
      notifiedMap[notifiedKey] = endsAt
      saveNotifiedMap(notifiedMap)
    }
  }

  const start = () => {
    if (timer) return
    timer = window.setInterval(() => {
      void checkFavorites()
    }, CHECK_INTERVAL_MS)
    void checkFavorites()
  }

  const stop = () => {
    if (timer) {
      window.clearInterval(timer)
      timer = null
    }
  }

  load()

  watch(
    [enabled, permission],
    ([isEnabled, currentPermission]) => {
      if (isEnabled && currentPermission === 'granted') {
        start()
      } else {
        stop()
      }
    },
    { immediate: true },
  )
})
